// One id per user action, reused when that same action is retried, so the OMS can replay the
// answer instead of doing the action twice.
export const newClientRequestId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // crypto.randomUUID needs a secure context; the admin may be served over plain http.
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10, 16).join(""),
  ].join("-");
};

// Keeps the id of the action in `ref` while the body stays the same (a retry) and makes a new one
// when the body changed (a different action).
export const requestIdFor = (ref, fingerprint) => {
  if (ref.current === null || ref.current.fingerprint !== fingerprint) {
    ref.current = { fingerprint, id: newClientRequestId() };
  }
  return ref.current.id;
};

// Call after a success so the next action starts with a fresh id.
export const forgetRequestId = (ref) => {
  ref.current = null;
};

// True when the answer does not say whether the service applied the action: no answer at all (a
// network error), a 5xx (the OMS also reports "the first attempt may still be running" as a 503
// SERVICE_UNAVAILABLE), or the idempotency code for that case, whatever status carries it. The same
// action must then be retried with the same id and the same body, and the service replays the answer
// it already stored.
export const isAmbiguousFailure = (result) =>
  !result.ok &&
  (result.httpStatus === undefined ||
    result.httpStatus === null ||
    result.httpStatus >= 500 ||
    result.code === "IDEMPOTENCY_IN_PROGRESS");

// Call with every answer. A success, or a definite refusal (an HTTP 4xx other than the idempotency
// "still running" code), ends the action, so its id is dropped; an ambiguous failure keeps the id.
export const settleRequestId = (ref, result) => {
  if (!isAmbiguousFailure(result)) {
    ref.current = null;
  }
};
